import React, { useRef, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import YouTube from "react-youtube";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import ShowSidebar from "../Sidebar/ShowSidebar";
import { Helmet } from "react-helmet";
import {
  getShowAwards,
  getShowDetailPage,
} from "../../../utils/api/helpers/show";
import ShowAwardsSidebarContainer from "../Awards/components/ShowAwardsSidebarContainer";
import { S3URL } from "../../../data/globalConstants";
import PageLoader from "../../../utils/Ui/PageLoader";
import { getShowMediaBySlug } from "../../../utils/api/helpers/media";
import CopyURL from "../../../utils/Ui/CopyURL";

export default function MediaDetail() {
  const { genreSlug, showSlug, sectionSlug, mediaSlug } = useParams();
  const location = useLocation();

  const containerRef = useRef(null);
  const bgImageRef = useRef(null);
  const showPageContentRef = useRef(null);
  const videoRef = useRef(null);
  const [showData, setShowData] = useState(null);
  const [showAwards, setShowAwards] = useState(null);
  const [hasAwards, setHasAwards] = useState(false);
  // const [activeSection, setActiveSection] = useState(sectionSlug || "");
  const [isLoading, setIsLoading] = useState(true);
  const [mediaSrc, setMediaSrc] = useState(null);
  const [mediaTitle, setMediaTitle] = useState(null);
  const [mediaDescription, setMediaDescription] = useState(null);
  const [mediaType, setMediaType] = useState(null);

  const vidOpts = {
    height: "390",
    width: "640",
    playerVars: {
      autoplay: 1,
      mute: 0,
      controls: 1,
      cc_load_policy: 0,
      fs: 1,
      iv_load_policy: 3,
      modestBranding: 1,
      rel: 0,
      showInfo: 0,
      playsInline: 1,
    },
  };

  useEffect(() => {
    if (!genreSlug || !showSlug || !mediaSlug) return;

    let isMounted = true;
    setIsLoading(true);

    const showDetailPromise = getShowDetailPage(genreSlug, showSlug).then(
      (res) => {
        if (!isMounted) return;
        setShowData(res.data);
      },
    );

    const mediaPromise = getShowMediaBySlug(showSlug, mediaSlug).then((res) => {
      if (!isMounted) return;
      if (res.data) {
        setMediaSrc(res.data.filename);
        setMediaTitle(res.data.title);
        setMediaDescription(res.data.description);
        setMediaType(res.data.type);
      }
    });

    Promise.allSettled([showDetailPromise, mediaPromise]).finally(() => {
      if (isMounted) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [genreSlug, showSlug, mediaSlug]);
  // useEffect(() => {
  //   if (!genreSlug || !showSlug || !mediaSlug) return;
  //
  //   getShowAwards(genreSlug, showSlug).then((res) => {
  //     if (res.data.competitions.length > 0) {
  //       setShowAwards(res.data.competitions);
  //       setHasAwards(true);
  //     }
  //   });
  // }, [genreSlug, showSlug, mediaSlug]);

  useGSAP(
    () => {
      const bgImg = bgImageRef.current;
      const showPage = showPageContentRef.current;

      if (bgImg && showPage) {
        // reset states each time
        gsap.set(bgImg, { opacity: 0, filter: "blur(5px) brightness(0.6)" });
        gsap.set(showPage, { opacity: 0, y: 20 });

        // timeline for smooth sequencing
        const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
        tl.to(bgImg, { opacity: 1, duration: 1.2 }).to(
          showPage,
          { opacity: 1, y: 0, duration: 1.2 },
          "-=0.6",
        ); // overlap slightly
      }
    },
    { dependencies: [showSlug, location.key], scope: containerRef }, // rerun on navigation
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <PageLoader loading={isLoading} />
      <div className="media-page-container" ref={containerRef}>
        {showData && showData?.title && mediaTitle && mediaDescription && (
          <Helmet>
            <title>{`${mediaTitle} | ${showData?.title} | Netflix Series FYC`}</title>
            <meta name="description" content={`${mediaDescription}`} />
          </Helmet>
        )}

        <div className="width-ctrl">
          <div
            className="show-page-inner"
            ref={showPageContentRef}
            // style={{ marginTop: "125px" }}
          >
            <div
              className="show-left-column"
              style={{
                top: !hasAwards ? "33%" : "",
                height: !hasAwards ? "fit-content" : "",
              }}
            >
              <ShowSidebar
                showData={showData}
                genreSlug={genreSlug}
                showSlug={showSlug}
                displayAwards={hasAwards}
                showAwards={showAwards}
              />
            </div>

            <div className="show-right-column">
              <div className="show-main-content">
                <section className="show-page-section mediadetail-section">
                  {mediaType === "application/pdf" && (
                    <div className={"pdf-container"}>
                      <iframe
                        title={mediaTitle}
                        style={{
                          position: "relative",
                          zIndex: 1,
                          minHeight: "500px",
                          height: "80vh",
                        }}
                        type="application/pdf"
                        className="d-none d-md-block titledetail__mediaalbum--script"
                        width="100%"
                        height="100%"
                        frameBorder="no"
                        src={
                          S3URL + mediaSrc + "#zoom=FitW&toolbar=0&navpanes=0"
                        }
                      ></iframe>
                    </div>
                  )}
                  {mediaType === "youtube" && (
                    <>
                      <div className="media-container">
                        <YouTube
                          videoId={mediaSrc}
                          opts={vidOpts}
                          ref={videoRef}
                        />
                      </div>
                      <div className="media-meta-container">
                        <div className="media-meta-title-wrapper">
                          <div className="media-meta-title">{mediaTitle}</div>
                          <div className="media-meta-actions">
                            <CopyURL />
                          </div>
                        </div>

                        <div
                          className="media-meta-description"
                          dangerouslySetInnerHTML={{ __html: mediaDescription }}
                        />
                      </div>
                    </>
                  )}
                </section>

                {hasAwards && (
                  <div className="d-block d-sm-none">
                    <ShowAwardsSidebarContainer
                      genreSlug={genreSlug}
                      showSlug={showSlug}
                      showAwards={showAwards}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Blurred + darkened background */}
        <div className="show-page-background" ref={bgImageRef}>
          <img
            src={S3URL + showData?.backgroundImage}
            loading="lazy"
            alt={showData?.title}
            className="show-masked-image"
          />
        </div>
      </div>
    </>
  );
}
